import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  ShieldAlert, Search, ChevronLeft, ChevronRight, Bot, Ban, Wifi, WifiOff, Paperclip,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import IncidentDetail from './IncidentDetail';
import { UserAvatar } from '@/components/UserAvatar';

// ─── Constants ───────────────────────────────────────────────────────────────

const CATEGORY_LABEL: Record<string, string> = {
  safety: 'Safety threat or violence',
  harassment: 'Harassment or rude behavior',
  reckless_driving: 'Reckless or dangerous driving',
  fraud: 'Fraud or fare gouging',
  vehicle_condition: 'Unsafe / dirty vehicle',
  off_route: 'Driver went off-route',
  no_show: 'No-show',
  other: 'Other',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-400 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  medium: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  low: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  under_review: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  dismissed: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  warning_issued: 'bg-blue-500/5 text-blue-300 border-blue-400/30',
  suspended: 'bg-red-500/10 text-red-400 border-red-500/20',
  escalated: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
};

const STATUS_LABEL: Record<string, string> = {
  open: 'Open',
  under_review: 'Under review',
  dismissed: 'Dismissed',
  warning_issued: 'Warning issued',
  suspended: 'Suspended',
  escalated: 'Escalated',
};

const SEVERITY_LABEL: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Incident {
  id: string;
  reporter_id: string | null;
  reporter_role: 'driver' | 'passenger';
  reporter_name: string | null;
  subject_id: string;
  subject_role: 'driver' | 'passenger';
  subject_name: string | null;
  subject_suspended_at: string | null;
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: string;
  description: string;
  evidence_urls: string[] | null;
  reporter_phone: string | null;
  reporter_picture_url: string | null;
  subject_phone: string | null;
  subject_license_plate: string | null;
  subject_picture_url: string | null;
  ride_id: string | null;
  is_system_generated: boolean;
  resolution_note: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Filter chips ────────────────────────────────────────────────────────────

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
          : 'bg-gray-800/50 text-gray-400 border-gray-700 hover:border-gray-600'
      }`}
    >
      {label}
    </button>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function Incidents() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const PAGE_SIZE = 50;

  // Filters
  const [statusFilters, setStatusFilters] = useState<string[]>(['open', 'under_review']);
  const [severityFilters, setSeverityFilters] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [hideSystem, setHideSystem] = useState(false);

  // Detail drawer
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const channelRef = useRef<any>(null);

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch a large batch and filter client-side for multi-select filters
      // The RPC only supports single status/severity filter, so we fetch broadly
      const params: any = {
        p_limit: 500,
        p_offset: 0,
      };
      // Only pass single filter to RPC if exactly one is selected
      if (statusFilters.length === 1) params.p_status = statusFilters[0];
      if (severityFilters.length === 1) params.p_severity = severityFilters[0];
      if (roleFilter !== 'all') params.p_subject_role = roleFilter;

      const { data, error } = await supabase.rpc('list_incident_reports_admin', params);
      if (error) throw error;

      let list: Incident[] = [];
      if (data && typeof data === 'object' && 'reports' in data) {
        list = (data as any).reports ?? [];
      } else if (Array.isArray(data)) {
        list = data;
      }

      // Apply client-side filters for multi-select
      if (statusFilters.length > 1) {
        list = list.filter((i) => statusFilters.includes(i.status));
      }
      if (severityFilters.length > 1) {
        list = list.filter((i) => severityFilters.includes(i.severity));
      }
      if (hideSystem) {
        list = list.filter((i) => !i.is_system_generated);
      }

      // Set total from filtered list, then paginate client-side
      setTotalCount(list.length);
      const paged = list.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
      setIncidents(paged);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilters, severityFilters, roleFilter, hideSystem]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  // ─── Realtime ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const channel = supabase
      .channel('admin-incidents')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'incident_reports' },
        (payload) => {
          setNewCount((c) => c + 1);
          const newRow = payload.new as any;
          if (newRow.severity === 'critical') {
            toast.error('Critical incident reported', {
              description: CATEGORY_LABEL[newRow.category] || newRow.category,
              duration: 8000,
            });
            // Play a soft chime
            try {
              const ctx = new AudioContext();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.frequency.value = 880;
              osc.type = 'sine';
              gain.gain.value = 0.1;
              osc.start();
              gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
              osc.stop(ctx.currentTime + 0.5);
            } catch {}
          }
          // Trigger a full refetch on first page so the new row includes
          // all joined fields (reporter/subject names, phones, avatars).
          // Prepending the raw Realtime payload would show blank joined fields.
          if (page === 0) {
            void fetchIncidents();
          }
        })
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'incident_reports' },
        (payload) => {
          setIncidents((prev) =>
            prev.map((i) => i.id === (payload.new as any).id ? { ...i, ...payload.new } as Incident : i)
          );
        })
      .subscribe((status) => {
        setRealtimeConnected(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
    };
  }, [page]);

  // ─── Search filter (client-side) ──────────────────────────────────────────
  // Note: search is applied to the already-paginated results.
  // For a full-text search across all incidents, the RPC would need a search param.
  const filtered = search
    ? incidents.filter((i) =>
        i.reporter_name?.toLowerCase().includes(search.toLowerCase()) ||
        i.subject_name?.toLowerCase().includes(search.toLowerCase()) ||
        i.description?.toLowerCase().includes(search.toLowerCase())
      )
    : incidents;

  // ─── Toggle helpers ────────────────────────────────────────────────────────

  const toggleStatus = (s: string) => {
    setStatusFilters((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
    setPage(0);
  };

  const toggleSeverity = (s: string) => {
    setSeverityFilters((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
    setPage(0);
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // ─── Handle detail close with refresh ──────────────────────────────────────

  const handleDetailClose = (refreshNeeded?: boolean) => {
    setDrawerOpen(false);
    setSelectedIncident(null);
    if (refreshNeeded) fetchIncidents();
  };

  return (
    <TooltipProvider>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              Trust & Safety
              {newCount > 0 && (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs ml-2">
                  {newCount} new
                </Badge>
              )}
            </h1>
            <p className="text-sm text-gray-400 mt-1">Incident triage queue: reports from passengers, drivers, and SOS auto-incidents</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Realtime indicator */}
            <Tooltip>
              <TooltipTrigger asChild>
                <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${
                  realtimeConnected
                    ? 'bg-emerald-500/5 text-emerald-400 border-emerald-500/20'
                    : 'bg-amber-500/5 text-amber-400 border-amber-500/20'
                }`}>
                  {realtimeConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                  {realtimeConnected ? 'Realtime' : 'Disconnected'}
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-gray-900 border-gray-700 text-white">
                {realtimeConnected ? 'Live updates active' : 'Reconnecting...'}
              </TooltipContent>
            </Tooltip>

            {newCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                onClick={() => { setNewCount(0); fetchIncidents(); }}
              >
                Load {newCount} new
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide mr-1">Status:</span>
            {Object.entries(STATUS_LABEL).map(([key, label]) => (
              <FilterChip
                key={key}
                label={label}
                active={statusFilters.includes(key)}
                onClick={() => toggleStatus(key)}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide mr-1">Severity:</span>
            {Object.entries(SEVERITY_LABEL).map(([key, label]) => (
              <FilterChip
                key={key}
                label={label}
                active={severityFilters.includes(key)}
                onClick={() => toggleSeverity(key)}
              />
            ))}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Role:</span>
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
                className="bg-gray-800/50 border border-gray-700 rounded-md px-2 py-1 text-xs text-gray-300"
              >
                <option value="all">Both</option>
                <option value="driver">Driver</option>
                <option value="passenger">Passenger</option>
              </select>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={hideSystem}
                onChange={(e) => { setHideSystem(e.target.checked); setPage(0); }}
                className="rounded border-gray-600 bg-gray-800 text-emerald-500 focus:ring-emerald-500/30"
              />
              Hide system-generated
            </label>
            <div className="flex-1" />
            <div className="relative max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <Input
                placeholder="Search names or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-xs bg-gray-900/50 border-gray-800 text-white placeholder:text-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 bg-gray-800/50 rounded" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-16 text-center">
                <ShieldAlert className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 font-medium">No incidents. Your platform is healthy.</p>
                <p className="text-gray-500 text-sm mt-1">Reports from passengers, drivers, and SOS alerts will appear here.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-transparent">
                    <TableHead className="text-gray-400 w-24">Severity</TableHead>
                    <TableHead className="text-gray-400 w-28">Status</TableHead>
                    <TableHead className="text-gray-400">Reporter</TableHead>
                    <TableHead className="text-gray-400">Subject</TableHead>
                    <TableHead className="text-gray-400">Category</TableHead>
                    <TableHead className="text-gray-400 w-28">Created</TableHead>
                    <TableHead className="text-gray-400 w-20 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((incident) => (
                    <TableRow
                      key={incident.id}
                      className="border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                      onClick={() => { setSelectedIncident(incident); setDrawerOpen(true); }}
                    >
                      <TableCell>
                        <Badge className={`${SEVERITY_COLORS[incident.severity]} text-xs`}>
                          {SEVERITY_LABEL[incident.severity] || incident.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLORS[incident.status]} text-xs`}>
                          {STATUS_LABEL[incident.status] || incident.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {incident.is_system_generated || !incident.reporter_id ? (
                            <>
                              <Bot className="w-3.5 h-3.5 text-gray-500" />
                              <span className="text-gray-400 text-sm">System</span>
                            </>
                          ) : (
                            <>
                              <UserAvatar
                                size="sm"
                                name={incident.reporter_name}
                                src={incident.reporter_picture_url}
                              />
                              <span className="text-white text-sm">{incident.reporter_name || 'Unknown'}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-gray-700 text-gray-500">
                                {incident.reporter_role}
                              </Badge>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            size="sm"
                            name={incident.subject_name}
                            src={incident.subject_picture_url}
                            ring={incident.subject_suspended_at ? 'red' : 'none'}
                          />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1.5">
                              <span className="text-white text-sm">{incident.subject_name || 'Unknown'}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-gray-700 text-gray-500">
                                {incident.subject_role}
                              </Badge>
                            </div>
                            {incident.subject_phone && (
                              <a
                                href={`tel:${incident.subject_phone.replace(/\s+/g, '')}`}
                                className="font-mono text-xs text-gray-500 hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {incident.subject_phone}
                              </a>
                            )}
                          </div>
                          {incident.subject_suspended_at && (
                            <Tooltip>
                              <TooltipTrigger>
                                <Ban className="w-3.5 h-3.5 text-red-400" />
                              </TooltipTrigger>
                              <TooltipContent className="bg-gray-900 border-gray-700 text-white text-xs">
                                Currently suspended
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-300 text-sm">
                        <div className="flex items-center gap-2">
                          <span>{CATEGORY_LABEL[incident.category] || incident.category}</span>
                          {incident.evidence_urls && incident.evidence_urls.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-800 text-gray-400 text-[10px]">
                              <Paperclip className="w-3 h-3" />
                              {incident.evidence_urls.length}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {formatDistanceToNow(new Date(incident.created_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs"
                          onClick={() => { setSelectedIncident(incident); setDrawerOpen(true); }}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                className="text-gray-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs text-gray-400">
                Page {page + 1} of {totalPages || 1}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="text-gray-400 hover:text-white"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Detail drawer */}
        {selectedIncident && (
          <IncidentDetail
            incident={selectedIncident}
            open={drawerOpen}
            onClose={handleDetailClose}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

export { CATEGORY_LABEL, SEVERITY_COLORS, SEVERITY_LABEL, STATUS_COLORS, STATUS_LABEL };
